import { Avatar, Flex } from '@chakra-ui/core'
import useAvatar from 'hooks/useAvatar'
import React, { memo, useCallback, useEffect, useMemo } from 'react'
import { Post } from 'redux/reducers/feed'
import { setSelectedChat } from 'redux/actions/feed'
import { RootState } from 'constants/interfaces'
import { useSelector, useDispatch } from 'react-redux'
import { getCurrentEpoch, getTextDirection } from 'utils'
import useComments from 'hooks/useComments'
import isSolid from 'is-solid'
import { ChatPreviewText, ChatPreviewContainer, ChatPreviewTitle, LastMessageBox } from './helperStyles'
import { TooltipList } from 'components/CommonComponents/TooltipList'
import { MdGroup } from 'react-icons/md'
import { IconList } from 'components/Post/IconList'
import { ChatModal } from 'components/Chat/ChatModal/ChatModal'
import { css } from '@emotion/core'
import { getDropShadowFilter, getFloatingAnimation } from 'components/NewsFeed/PersonalView/utils'

interface ChatPreviewProps {
  chat: Post
  isPersonalView?: boolean
  isAllowToNavigate?: boolean
  isFaded?: boolean
  colorIndex?: number
}

export const ChatPreview = memo(
  ({ chat, isAllowToNavigate = true, isPersonalView = false, isFaded = false, colorIndex = 0 }: ChatPreviewProps) => {
    const { post_id, title, images } = chat
    const dispatch = useDispatch()
    const theme = useSelector((state: RootState) => state.general.theme)
    const selectedChatID = useSelector((state: RootState) => state.feed.selectedChatID)
    const { fetchComments, fetchCommentsCounter, fetchCommentsLoading } = useComments(post_id)
    const chatMessages = useSelector((state: RootState) => state.comments.commentsState?.[post_id]?.items ?? [])
    const viewedCommentsIds = useSelector((state: RootState) => state.comments.viewedCommentsIds?.[post_id] ?? [])
    const isOpen = selectedChatID === post_id
    const lastMessage = chatMessages && chatMessages.length > 0 ? chatMessages.slice(-1)[0] : null
    const isLastMessageUnred =
      lastMessage && viewedCommentsIds.length > 0 && !fetchCommentsLoading
        ? !viewedCommentsIds.includes(lastMessage?.cid)
        : false

    const avatar = useAvatar(lastMessage?.created_by)

    const lastMessageMedia = {
      audios: lastMessage?.audios || [],
      videos: lastMessage?.videos || [],
      files: lastMessage?.files || [],
      images: lastMessage?.images || [],
    }

    const hasNonEmptyMedia = Object.values(lastMessageMedia).some((mediaArray) => mediaArray.length > 0)
    const hasUnreadMessages = !isFaded && isLastMessageUnred
    const shadowFilter = useMemo(() => getDropShadowFilter(colorIndex), [colorIndex])
    const floatingAnimation = useMemo(() => getFloatingAnimation(colorIndex), [colorIndex])

    const getComments = useCallback(
      async (isManuallyTriggered?: boolean, id?: string) => {
        try {
          let timestampFromToGetComments = getCurrentEpoch()

          if (chatMessages && chatMessages?.length > 0) {
            timestampFromToGetComments = chatMessages[0].created_at_ts
          }
          const commentsFromOrigin = await fetchComments({
            limit: 12,
            created_ts: timestampFromToGetComments,
            isManuallyTriggered,
            id,
          })
          if (!isSolid(commentsFromOrigin)) {
            return
          }
        } catch (error) {
          console.log(error)
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [chatMessages]
    )

    useEffect(() => {
      if (chatMessages && chatMessages.length > 0) return
      getComments()
      fetchCommentsCounter()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [post_id])

    const openChatModal = () => {
      if (!isAllowToNavigate) {
        return
      }
      dispatch(setSelectedChat(post_id))
    }

    const onClose = () => {
      dispatch(setSelectedChat(null))
    }
    return (
      <>
        <ChatPreviewContainer
          style={{ justifyContent: !isPersonalView ? 'center' : 'flex-start' }}
          onClick={openChatModal}
          mt={isPersonalView ? '0' : '24px'}
          css={css`
            opacity: ${isFaded ? 0.5 : 1};
          `}
        >
          <Flex
            css={
              isPersonalView
                ? css`
                    transition: transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1);
                    transform-origin: center;

                    &:hover {
                      transform: scale(1.1);
                    }
                    &:hover > div {
                      animation-play-state: paused;
                    }
                  `
                : css`
                    transition: transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1);
                    transform-origin: center;
                    &:hover {
                      transform: scale(1.1);
                    }
                  `
            }
          >
            <Flex
              flexDirection="column"
              alignItems="center"
              position="relative"
              css={
                isPersonalView
                  ? css`
                      animation: ${floatingAnimation.pattern} ${floatingAnimation.duration}s ease-in-out
                        ${floatingAnimation.delay}s infinite;
                      will-change: transform;
                    `
                  : undefined
              }
            >
              {isLastMessageUnred && lastMessage && (
                <>
                  <LastMessageBox
                    isPersonalView={isPersonalView}
                    dir={getTextDirection(lastMessage?.message)}
                    style={{ top: isPersonalView ? '22px' : '-10px', left: isPersonalView ? '5px' : 'unset' }}
                  >
                    {hasNonEmptyMedia ? (
                      <IconList {...lastMessageMedia} w="20px" h="20px" containerWidth="100%" />
                    ) : (
                      <ChatPreviewText>{lastMessage?.message}</ChatPreviewText>
                    )}
                  </LastMessageBox>
                  <Avatar
                    src={avatar}
                    pos="absolute"
                    top={isPersonalView ? '65px' : '35px'}
                    left={isPersonalView ? '0px' : '33px'}
                    zIndex={1000}
                    size={isPersonalView ? 'md' : 'sm'}
                    borderRadius="full"
                    boxShadow="-1px 1px 3px 1px #8b8b8b"
                  />
                </>
              )}

              <Flex
                border={isLastMessageUnred && !isPersonalView ? `2px solid ${theme?.elementsColor}` : '2px solid white'}
                borderRadius="50%"
                cursor="pointer"
                justifyContent="center"
                alignItems="center"
                bg="#A0AEC0"
                w={isPersonalView ? '112px' : '63px'}
                h={isPersonalView ? '112px' : '63px'}
                css={css`
                  ${hasUnreadMessages && isPersonalView ? `filter: ${shadowFilter};` : ''}
                `}
              >
                {images?.[0] ? (
                  <Avatar
                    w={isPersonalView ? '110px' : '61px'}
                    h={isPersonalView ? '110px' : '61px'}
                    src={images?.[0]}
                  />
                ) : (
                  <MdGroup
                    style={{
                      width: isPersonalView ? '60px' : '45px',
                      height: isPersonalView ? '60px' : '45px',
                      fill: 'white',
                    }}
                  />
                )}
              </Flex>
              <TooltipList items={[title]}>
                <ChatPreviewTitle isTruncated dir={getTextDirection(title)}>
                  {title}
                </ChatPreviewTitle>
              </TooltipList>
            </Flex>
          </Flex>
        </ChatPreviewContainer>
        <ChatModal isOpen={isOpen} onClose={onClose} chat={chat} />
      </>
    )
  }
)
